import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { Circle, ChevronDown, ChevronUp } from 'lucide-react';

import PropertyTitle from './ui/PropertyTitle';
import PropertyLocation from './ui/PropertyLocation';
import PropertyDomainStatus from './PropertyDomainStatus';

import {
  extractTemasSummary,
  extractEntidadesCounts,
  extractLegalCounts,
} from '../../utils/propertyUtils';

const Dot = ({ color = '#9CA3AF', title = '' }) => (
  <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <Circle size={8} color={color} fill={color} />
  </span>
);

const POSITIVE_THEME_COLORS = {
  default: '#DC2626',
  PRA: '#FACC15',
};

const ThemeItem = ({ label, count, enabled, positiveColor, zeroColor }) => {
  // enabled=false -> cinza/indisponível; count>0 -> cor específica; count===0 -> verde
  const isEnabled = enabled !== false;
  const defaultPositive = POSITIVE_THEME_COLORS[label] || POSITIVE_THEME_COLORS.default;
  const resolvedPositive = positiveColor || defaultPositive;
  const resolvedZero = zeroColor || '#16A34A';
  let color = '#9CA3AF';
  let texto = '—';
  if (isEnabled) {
    const n = typeof count === 'number' ? count : null;
    if (n === null) {
      color = '#9CA3AF';
      texto = '—';
    } else if (n > 0) {
      color = resolvedPositive;
      texto = String(n);
    } else {
      color = resolvedZero;
      texto = '0';
    }
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Dot color={color} title={texto} />
      <Typography variant="body2" color="text.secondary">
        {label}: {texto}
      </Typography>
    </Box>
  );
};

const Section = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{ p: 1.25, display: 'inline-block', maxWidth: '100%' }}
    className="rounded-lg border border-gray-200 bg-white"
  >
    <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const PropertySummaryDashboard = ({ propertyData, onDomainClick }) => {
  const temas = extractTemasSummary(propertyData);
  const entidades = extractEntidadesCounts(propertyData);
  const legal = extractLegalCounts(propertyData);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);

  const hasTemas = Array.isArray(propertyData?.properties?.sobreposicao)
    ? propertyData.properties.sobreposicao.length > 0
    : Array.isArray(propertyData?.properties?.temas) && propertyData.properties.temas.length > 0;
  const biomaPredominante = temas?.biomaPredominante || '--';

  return (
    <Box>
      {/* Toggle cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
        <button
          onClick={() => setHeaderCollapsed((v) => !v)}
          className="text-xs text-gray-600 hover:text-green-700 inline-flex items-center"
        >
          {headerCollapsed ? (
            <ChevronDown size={14} className="mr-1" />
          ) : (
            <ChevronUp size={14} className="mr-1" />
          )}
          {headerCollapsed ? 'Mostrar' : 'Ocultar'} cabeçalho
        </button>
      </Box>

      {/* Linha 1: Identificação | Legal | Entidades | Temas (compacto e auto-largura) */}
      {!headerCollapsed && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.0, alignItems: 'flex-start' }}>
          {/* Identificação: ocupa apenas o necessário, evita quebras */}
          <Box sx={{ flex: '0 1 auto', width: { xs: '100%', md: 'fit-content' } }}>
            <Section title="Identificação">
              <Box sx={{ mb: 0.25 }}>
                <PropertyTitle propertyData={propertyData} />
                <PropertyLocation propertyData={propertyData} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Bioma: <span style={{ fontWeight: 600 }}>{biomaPredominante}</span>
              </Typography>
            </Section>
          </Box>

          {/* Legal: compacto */}
          <Box sx={{ flex: '0 1 auto', width: { xs: '100%', md: 'fit-content' } }}>
            <Section title="Legal">
              <Box className="grid grid-cols-2 gap-1">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Cíveis
                  </Typography>
                  <Typography variant="subtitle1" sx={{ lineHeight: 1.05 }}>
                    {legal.totalCivis}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Criminais
                  </Typography>
                  <Typography variant="subtitle1" sx={{ lineHeight: 1.05 }}>
                    {legal.totalCriminais}
                  </Typography>
                </Box>
              </Box>
            </Section>
          </Box>

          {/* Entidades: compacto */}
          <Box sx={{ flex: '0 1 auto', width: { xs: '100%', md: 'fit-content' } }}>
            <Section title="Entidades">
              <Box className="grid grid-cols-3 gap-1">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="subtitle1" sx={{ lineHeight: 1.05 }}>
                    {entidades.total}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    PF
                  </Typography>
                  <Typography variant="subtitle1" sx={{ lineHeight: 1.05 }}>
                    {entidades.pf}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    PJ
                  </Typography>
                  <Typography variant="subtitle1" sx={{ lineHeight: 1.05 }}>
                    {entidades.pj}
                  </Typography>
                </Box>
              </Box>
            </Section>
          </Box>

          {/* Sobreposições: duas colunas, largura auto */}
          <Box sx={{ flex: '0 1 auto', width: { xs: '100%', md: 'fit-content' } }}>
            <Section title="Sobreposições">
              <Box className="grid grid-cols-2 gap-x-6 gap-y-1">
                <ThemeItem label="UC" count={temas?.counts?.uc} enabled={hasTemas} />
                <ThemeItem
                  label="Quilombolas"
                  count={temas?.counts?.quilombolas}
                  enabled={hasTemas}
                />
                <ThemeItem label="Aldeias" count={temas?.counts?.aldeias} enabled={hasTemas} />
                <ThemeItem
                  label="Assentamentos"
                  count={temas?.counts?.assentamentos}
                  enabled={hasTemas}
                />
                <ThemeItem label="Florestas" count={temas?.counts?.florestas} enabled={hasTemas} />
                <ThemeItem
                  label="Embargos IBAMA"
                  count={temas?.counts?.embargos_ibama}
                  enabled={hasTemas}
                />
                <ThemeItem label="Mineração" count={temas?.counts?.mineracao} enabled={hasTemas} />
                <ThemeItem
                  label="Terras Indígenas"
                  count={temas?.counts?.terras_indigenas}
                  enabled={hasTemas}
                />
                <ThemeItem label="PRA" count={temas?.counts?.pra} enabled={hasTemas} />
                <ThemeItem
                  label="Cursos d'água"
                  count={temas?.counts?.curso_dagua}
                  enabled={hasTemas}
                  positiveColor="#16A34A"
                  zeroColor="#9CA3AF"
                />
                <ThemeItem
                  label="Cursos d'água - Trechos"
                  count={temas?.counts?.curso_dagua_trecho}
                  enabled={hasTemas}
                  positiveColor="#16A34A"
                  zeroColor="#9CA3AF"
                />
                <ThemeItem
                  label="Massas d'água"
                  count={temas?.counts?.massa_dagua}
                  enabled={hasTemas}
                  positiveColor="#16A34A"
                  zeroColor="#9CA3AF"
                />
                <ThemeItem
                  label="Rios"
                  count={temas?.counts?.rios}
                  enabled={hasTemas}
                  positiveColor="#16A34A"
                  zeroColor="#9CA3AF"
                />
              </Box>
            </Section>
          </Box>
        </Box>
      )}

      {/* Separador sutil antes de Domínios */}
      <Divider sx={{ my: 1 }} />

      {/* Toggle e Domínios */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Domínios
        </Typography>
        <button
          onClick={() => setSummaryCollapsed((v) => !v)}
          className="text-xs text-gray-600 hover:text-green-700 inline-flex items-center"
        >
          {summaryCollapsed ? (
            <ChevronDown size={14} className="mr-1" />
          ) : (
            <ChevronUp size={14} className="mr-1" />
          )}
          {summaryCollapsed ? 'Mostrar' : 'Ocultar'} domínios
        </button>
      </Box>
      {!summaryCollapsed && (
        <Box>
          <PropertyDomainStatus propertyData={propertyData} onDomainClick={onDomainClick} />
        </Box>
      )}
    </Box>
  );
};

export default PropertySummaryDashboard;
